import { parseNumber } from "../helpers/parse.helper";
import { QueryParams, QueryResult } from "../query/query.data";

export interface DocumentBase {
  id: number;
  uploadDate: Date;
  filename: string;
  length: number;
  customerId: number;
  category: DocumentCategory;
  mimeType: string;
  name: string;
  description: string;
  uploadedBy: string;
  canDelete: boolean;
  // The downloadable file(s) that make up this one logical entry (server-provided). A statement
  // returns its PDF + XLSX here; ordinary documents return a single rendition (RYNO-14664).
  renditions?: DocumentRendition[];
}

export interface Document extends DocumentBase {
  loanId: number;
}

export enum DocumentCategory {
  Other = "other",
  Contract = "contract",
  Application = "application",
  ApplicationSupporting = "application-supporting",
  CustomAgreement = "custom-agreement",
  InsurancePolicy = "insurance-policy",
  BankStatement = "bank-statement",
  TaxReturn = "tax-return",
  Disclosure = "disclosure",
  SettlementStatement = "settlement-statement",
  Statement = "statement",
  UCCFiling = "ucc-filing"
}

export const DocumentCategoryLabel = new Map<DocumentCategory, string>([
  [DocumentCategory.Other, "Other"],
  [DocumentCategory.Contract, "Contract"],
  [DocumentCategory.Application, "Application"],
  [DocumentCategory.ApplicationSupporting, "Application supporting"],
  [DocumentCategory.CustomAgreement, "Custom agreement"],
  [DocumentCategory.InsurancePolicy, "Insurance policy"],
  [DocumentCategory.BankStatement, "Bank Statement"],
  [DocumentCategory.TaxReturn, "Tax return"],
  [DocumentCategory.Disclosure, "Disclosure"],
  [DocumentCategory.SettlementStatement, "Settlement statement"],
  [DocumentCategory.Statement, "Statement"],
  [DocumentCategory.UCCFiling, "UCC filing"]
]);

export class DocumentQueryParams extends QueryParams {
  text?: string;
  fromDate?: any;
  toDate?: any;
  category?: any;

  constructor(params?: any) {
    super(params);
    params = params || {};

    this.text = params.text || undefined;
    this.fromDate = params.fromDate || undefined;
    this.toDate = params.toDate || undefined;
    this.category = params.category || undefined;
  }
}

export class LoanDocumentQueryParams extends DocumentQueryParams {
  loanId?: number;
  loanUniqueId?: string;

  constructor(params?: any) {
    super(params);
    params = params || {};

    this.loanId = parseNumber(params.loanId) || undefined;
    this.loanUniqueId = params.loanUniqueId || undefined;
  }
}

export interface DocumentQueryResult extends QueryResult<Document> {}

export const PDF_MIME_TYPE = "application/pdf";
export const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** A downloadable file of a logical entry, as provided by the server (paired by group id). */
export interface DocumentRendition {
  id: number;
  mimeType: string;
}

/** A rendition decorated for display — download-button icon + tooltip. */
export interface DecoratedDocumentRendition extends DocumentRendition {
  icon: string;
  tooltip: string;
}

const decorate = (rendition: DocumentRendition): DecoratedDocumentRendition => {
  switch (rendition.mimeType) {
    case PDF_MIME_TYPE:
      return { ...rendition, icon: "picture_as_pdf", tooltip: "Download PDF" };
    case XLSX_MIME_TYPE:
      return { ...rendition, icon: "grid_on", tooltip: "Download Excel" };
    default:
      return { ...rendition, icon: "get_app", tooltip: "Download" };
  }
};

/**
 * The decorated download options for one logical entry. Uses the server's renditions (paired by
 * group id — a statement's PDF + XLSX), falling back to the document itself when none are supplied
 * (ordinary/older documents). Never pairs by name/filename (RYNO-14664).
 */
export function documentRenditions(document: Document): DecoratedDocumentRendition[] {
  const renditions = document?.renditions?.length
    ? document.renditions
    : [{ id: document?.id, mimeType: document?.mimeType }];
  return renditions.map(decorate);
}

export class DocumentFilters {
  text: string;
  fromDate: Date;
  toDate: Date;
  category: DocumentCategory;

  constructor(filters?) {
    filters = filters || {};
    this.text = filters.text || undefined;
    this.fromDate = filters.fromDate || undefined;
    this.toDate = filters.toDate || undefined;
    this.category = filters.category !== null ? filters.category : undefined;
  }

  remove(item: string) {
    this[item] = undefined;
    if (item === "fromDate") {
      this.fromDate = undefined;
      this.toDate = undefined;
    }
  }
}
